/*
 * Attachment component
 */

import React, {useState, useRef} from 'react';

import Base from './base.jsx';
import Helpers from './helpers.jsx'
import Setting from "./setting.jsx";

import {Portal} from '../../common/Portal.jsx';
import {Modal as ModalComponent, closeModal} from '../../common/Modal.jsx';
import Errors from '../../common/Errors.jsx';
import {Mode, castDataType, DataType} from '../common.jsx';

 import { update } from "../../../helpers/index.js";

import { Dashboard } from "@uppy/react";
import uploadManager from "../../../helpers/UploadManager.js";

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'

import '@uppy/drag-drop/dist/style.css'
import '@uppy/file-input/dist/style.css'
import '@uppy/progress-bar/dist/style.css'

const MODAL_ID = 'add-missing-attachment-modal';

export default class Attachment extends Base {

        constructor(props, context) {
            super(props);

            let initialFiles = []

            if (!(props.hideSensitiveInformation && props.content.containsSensitiveInformation) && [Mode.FILL, Mode.VIEW].includes(this.props.mode)){
              if (uploadManager.useUpdatedAttachmentComponent()) {
                  initialFiles = this.getRawAnswer(context) || [];
              } else
              [this.getRawAnswer(context)]
              .flat()
              .forEach((a) => {
                  const f = props.attachments[a] || this.getPrefilledAttachments(context).find((f) => f.attachment_attr == a);
                  if (f) { 
                    initialFiles.push({ ...f, strId: a.split(":")[props.repeatedFieldScope ? 2 : 1] || "" });
                  }

                  if (((props.attachments || {})['new'] || {})[a]) {
                    const strId = a.split(":")[props.repeatedFieldScope ? 2 : 1] || ""
                    if(!initialFiles.some( f => f.strId == strId)){
                        initialFiles.push(
                            {
                              ...props.attachments.new[a], 
                              status: 'success', 
                              strId: strId,
                              uploadId: props.attachments.new[a].upload_id
                            }
                        )
                    }
                  }                  
              })
            }

            this.state = {
                virusScanState: "NOT_STARTED", // 'NOT_STARTED' | 'PROCESSING' | 'FINISHED'
                virusScanResult: "OK", // 'OK' || 'INFECTED' || 'FAILED'
                files: initialFiles,
                deletedFiles: [],
                uploadProgress: {},
                pendingUploads: [],
                uppy: null,
                error: null,
                isModalOpen: false,
            };
            this.uppyId = null;
            this._isMounted = false;
            this.fileSubscriptions = new Map(); // Store subscriptions per file

            this.pollVirusScanResult = this.pollVirusScanResult.bind(this);
            this.renderVirusScanState = this.renderVirusScanState.bind(this);
            this.renderHiddenInputs = this.renderHiddenInputs.bind(this);
            this.renderAttachments = this.renderAttachments.bind(this);

            this.isLocked =  this.props.content && this.isReadOnlyAnswer();

            this.dropzoneRef = React.createRef();
          }

        static get CATEGORY() {
            return 'question';
        }

        static get TYPE() {
            return 'attachment';
        }

        static icon(){
            return <i className="fa fa-paperclip"></i>;
        }

        static get FORMATS() {
            return {
                MS_WORD: [
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                ],
                MS_EXCEL: [
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                ],
                MS_POWERPOINT: [
                    'application/vnd.ms-powerpoint',
                    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                ],
                OO_WRITER: [
                    'application/vnd.oasis.opendocument.text'
                ],
                OO_CALC: [
                    'application/vnd.oasis.opendocument.spreadsheet'
                ],
                OO_IMPRESS: [
                    'application/vnd.oasis.opendocument.presentation'
                ],
                PDF: [
                    'application/pdf'
                ],
                RTF: [
                    'application/rtf'
                ],
                IMAGE: [
                    'image/*'
                ],
                PLAIN_TEXT: [
                    'text/*'
                ]
            }
        }

        static get FORMAT_EXTENSIONS() {
            return {
                MS_WORD: '.docx, .doc',
                MS_EXCEL: '.xlsx, .xls',
                MS_POWERPOINT: '.pptx, .ppt',
                OO_WRITER: '.odt',
                OO_CALC: '.ods',
                OO_IMPRESS: '.odp',
                PDF: '.pdf',
                RTF: '.rtf',
                IMAGE: '.jpg, .png, .bmp, ...',
                PLAIN_TEXT: '.txt, ...'
            }
        }

        static get FILE_SIZE_UNITS() {
            return { tb: "TB", gb: "GB", mb: "MB", kb: "KB", b: "b" };
        } 

        static get TRANSLATED_ATTRIBUTES() {
            return {
                label: 'field_default_label',
                description: 'field_default_description',
                help: null
            };
        }

        static defaultContent(language) {
            return Object.assign(super.defaultContent(language), {
                label: 'label',
                description: 'description',
                name: 'field_' + Helpers.randomString(),
                help: 'help',
                required: false,
                label_hidden: false,
                acceptAnyFormat: true,
                fileFormats: [],
                reportKey: '',
                maxFiles: 1,
                prettyView: {
                    hide: false,
                    show_label: false
                },
            });
        }

        renderView() {
            if (uploadManager.useUpdatedAttachmentComponent()) {
              return this.renderViewV2();
            }

            const { virusScannerEnabled } = this.props;
            const { virusScanState } = this.state;

            return (
                // Relative position for the counter
                <div style={{ position: "relative"}}>
                    <label className={this.props.content.label_hidden ? 'hidden-label': ''}>
                        <span className="field-label">{this.getText('label')}</span>
                        {this.props.content.required ? <span className='required-field'> *</span> : null}
                        {this.helpIcon()}
                        {this.renderPrefillIcon()}
                    </label>

                    {this.renderFileCounter()}

                    <div ref={this.dropzoneRef} className={`dropzone attachment-dropzone ${this.props.mode == Mode.FILL && this.isLocked ? 'printable-dropzone' : ''} ${virusScannerEnabled && (virusScanState !== 'NOT_STARTED') ? ' virus-scan-enabled' : ''}`} id={'dz-' + Helpers.randomString()} name={this.fullFieldName()}>
                        {this.props.mode == Mode.FILL && this.isLocked ? null : <p className="drop-instruction dz-default dz-message">{I18n.t('attachment_drop_instruction', {scope: 'fe', locale: this.props.language})}</p>}
                        {this.renderFiles()}
                        {virusScannerEnabled ? this.renderVirusScanState(): null}
                        <input type='hidden' name={this.fullFieldName(true)} value={undefined}></input>
                    </div>

                    {this.renderDeletedFileInputs()}
                    
                    <div className="preview-template" style={{display: 'none'}}>
                        <div style={{ display: 'none'}} />
                    </div>
                    {this.descriptionText()}
                    {(this.props.content.fileFormats || []).length > 0 ? <p className="description">{I18n.t('accepted_file_formats', {
                        scope: 'fe',
                        formats: this.acceptedFormatList()
                    })}</p> : null}
                </div>
            );
        }

        renderPrettyView() {
            if (uploadManager.useUpdatedAttachmentComponent()) {
              return this.renderPrettyViewV2();
            }

            const {files} = this.state;
            const { attachmentDownloadUrl, attachmentShowDownloadLink } = this.props;
            const { hide = false, show_label = false } = this.props.content.prettyView || {};
            return ( hide ? null :
                <>
                    {show_label &&
                        <label>
                            <span className="field-label">{this.getText('label')}</span>
                        </label>
                    }
                    {files.map((f,i) => {
                        const hasError = f.status == "error" || f.status == "canceled";
                        const name =
                        !f.uploadId && attachmentDownloadUrl ? (
                            <a
                                href={f.dl_path || this.getDownloadUrl(f)}
                                data-turbo="false"
                                data-av="true"
                                data-av-content-type={f.content_type}
                                data-av-show-download-link={attachmentShowDownloadLink}
                                data-av-title={f.name}
                            >
                                {f.name}
                            </a>
                        ) : (
                            f.name
                        );
                        return (
                            <div
                            key={f.strId || f.filesystem_name}
                            className={`dz-preview dz-file-preview ${f.status == "added" ? "dz-processing" : f.status == "success" ? "dz-success" : hasError ? "dz-error" : ""}`}
                        >
                                <div className="dz-details">
                                    <i className='fa fa-image mr-1' />
                                    <span className="dz-filename">{name}</span>
                                </div>
                            </div>
                        )
                    })}
                </>
              )
          }

        renderFileCounter() {
            if (uploadManager.useUpdatedAttachmentComponent()) {
              return this.renderFileCounterV2();
            }

            const { files } = this.state;
            const maxFiles = this.getMaxFiles();

            return maxFiles && this.props.mode != Mode.VIEW && !this.isLocked ? (
                <span className="attachment-counter-count">
                    <span className={`label label-${files.length < maxFiles ? "default" : "info"}`}>
                        <span className="current">{files.length}</span> / <span className="limit">{maxFiles}</span> {I18n.t("files", { scope: "fe" }).toLowerCase()}
                    </span>
                </span>
            ) : null;
        }

        renderFiles() {
            const { files, uploadProgress } = this.state;
            const { attachmentDownloadUrl, attachmentShowDownloadLink } = this.props;

            return files.map((f, i) => {
                const { size, unit } = this.getFileSize(f.size);
                const hasError = f.status == "error" || f.status == "canceled";
                const name =
                    !f.uploadId && attachmentDownloadUrl ? (
                        <a
                            href={f.dl_path || this.getDownloadUrl(f)}
                            data-turbo="false"
                            data-av="true"
                            data-av-content-type={f.content_type}
                            data-av-show-download-link={attachmentShowDownloadLink}
                            data-av-title={f.name}
                        >
                            {f.name}
                        </a>
                    ) : (
                        f.name
                    );

                return (
                    <div
                        // Use filesystem name for attachments created before support for multiple files as they don't have string ID.
                        key={f.strId || f.filesystem_name}
                        className={`dz-preview dz-file-preview ${f.status == "added" ? "dz-processing" : f.status == "success" ? "dz-success" : hasError ? "dz-error" : ""}`}
                    >
                        <div className="dz-progress">
                            <span className="dz-upload" data-dz-uploadprogress style={{width: uploadProgress[f.uuid] + "%"}}></span>
                        </div>
                        <div className="dz-details">
                            <span className="dz-success-mark">
                                <span>✔</span>
                            </span>
                            <span className="dz-error-mark">
                                <span>✘</span>
                            </span>
                            <span className="dz-filename">{name}</span>
                            {f.uploadId && f.size ? (
                                <span className="dz-size" data-dz-size>
                                    <strong>{size}</strong> {unit}
                                </span>
                            ) : null}
                            {/* <span className="dz-progress-number">{ I18n.t('comment_attachment_upload') + (uploadProgress[f.uuid] ? uploadProgress[f.uuid].toFixed(2) : 0) + "%"}</span> */}
                            {!this.isLocked && (
                                <a href="#" className="btn btn-danger" data-dz-remove onClick={(e) => this.deleteFile(e, i)}>
                                    <i className="icon-trash"></i>
                                </a>
                            )}
                        </div>
                        {f.error ? (
                            <div className="dz-error-message">
                                <span data-dz-errormessage>{f.error}</span>
                            </div>
                        ) : null}
                        {f.uploadId && !hasError ? (
                            <input type="hidden" value={f.uploadId} name={this.uploadInputNamePrefix() + "[new][" + this.fileFieldName(f) + "][upload_id]"} />
                        ) : null}
                        {f.uploadId && !hasError ? <input type="hidden" value={f.uuid} name={this.uploadInputNamePrefix() + "[new][" + this.fileFieldName(f) + "][uuid]"} /> : null}
                        {f.uploadId && !hasError ? <input type="hidden" value={f.name} name={this.uploadInputNamePrefix() + "[new][" + this.fileFieldName(f) + "][name]"} /> : null}
                        {f.uploadId && !hasError ? <input type="hidden" value={f.strId} name={this.uploadInputNamePrefix() + "[new][" + this.fileFieldName(f) + "][strId]"} /> : null}
                        {f.uploadId && !hasError && f.upload_status == 'complete' ? <input type="hidden" value={true} name={this.uploadInputNamePrefix() + "[new][" + this.fileFieldName(f) + "][success]"} /> : null}
                        {f.attachment_attr && f.scope ? (
                            <>
                                <input type="hidden" value={f.attachment_attr} name={this.uploadInputNamePrefix() + "[prefill][" + this.fileFieldName(f) + "]prefill-from-field"} />
                                <input type="hidden" value={f.scope} name={this.uploadInputNamePrefix() + "[prefill][" + this.fileFieldName(f) + "]prefill-from-scope"} />
                            </>
                        ) : null}
                        {!hasError ? <input type="hidden" className="attachment-pointer" name={this.fullFieldName(true)} value={this.fileFieldName(f)} /> : null}
                    </div>
                );
            });
        }

        renderDeletedFileInputs() {
            const { deletedFiles } = this.state;

            return deletedFiles.map((f) => <input key={f.strId || f.filesystem_name} type="hidden" name={this.uploadInputNamePrefix() + "[deleted][]"} value={this.fileFieldName(f)} />);
        }


        renderVirusScanState() {
            const { virusScanState, virusScanResult } = this.state;

            if (virusScanState === 'NOT_STARTED') {
                return null;
            } else if (virusScanState === 'PROCESSING') {
                return <div className="virus-scan-status"><i className="fa fa-gear fa-spin"></i> Scanning for viruses</div>;
            } else {
                switch (virusScanResult) {
                case 'OK':
                    return <div className="virus-scan-status"><span className="success-mark">✔</span> Virus scan finished. No infected files found.</div>;
                case 'INFECTED':
                    if (this._dz.files.length >= 1) this._dz.removeFile(this._dz.files[0]);
                    return <div className="virus-scan-status file-rejected"><span>✘</span> Virus scan finished. Infected files were found and the upload was rejected.</div>
                case 'FAILED':
                default:
                    if (this._dz.files.length >= 1) this._dz.removeFile(this._dz.files[0]);
                    return <div className="virus-scan-status file-rejected"><span>✘</span> Virus scan failed. Please reupload the file.</div>;
                }
            }
        }


        renderSettings() {
            let containsSensitiveInformation = this.props.content.containsSensitiveInformation ? true : false;
            let acceptAnyFormat = (typeof this.props.content.acceptAnyFormat == 'undefined') ? true : this.props.content.acceptAnyFormat;
            let selectedFormats = this.props.content.fileFormats || [];
            let formatDefs = [
                {
                    title: I18n.t('file_format_documents', {scope: 'fe'}),
                    formats: ['PDF', 'MS_WORD', 'OO_WRITER', 'RTF', 'PLAIN_TEXT']
                },
                {
                    title: I18n.t('file_format_spreadsheets', {scope: 'fe'}),
                    formats: ['MS_EXCEL', 'OO_CALC']
                },
                {
                    title: I18n.t('file_format_presentations', {scope: 'fe'}),
                    formats: ['MS_POWERPOINT', 'OO_IMPRESS']
                },
                {
                    title: I18n.t('file_format_other', {scope: 'fe'}),
                    formats: ['IMAGE']
                }
            ];
            let formatOptions = (
                <div>
                    {formatDefs.map((formatDef, i) => {
                        return <div key={i} className={'checkbox-list-group' + (acceptAnyFormat ? ' disabled' : '')}>
                            <small>{formatDef.title}</small>
                            <div className="checkbox-list">
                                {formatDef.formats.map((format) => {
                                    return <label key={format}>
                                        <input type="checkbox" name="fileFormats" defaultChecked={selectedFormats.indexOf(format) != -1} defaultValue={format} disabled={acceptAnyFormat} onChange={this.updateSettings} />
                                        {I18n.t('file_format_' + format.toLowerCase(), {scope: 'fe'})}
                                        {Attachment.FORMAT_EXTENSIONS[format] ? <span className="description"> ({Attachment.FORMAT_EXTENSIONS[format]})</span> : null}
                                    </label>;
                                })}
                            </div>
                        </div>;
                    })}
                </div>
            );
            return (
                <div>
                    <Setting.Checkbox name="required" label={I18n.t('field_required', {scope: 'fe'})} value={this.props.content.required} onChange={this.updateSettings} />
                    <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
                    <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />

                    {!this.props.settings['hideDescriptionField'] &&
                      <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings}/>
                    }
                    {!this.props.settings['hideDescriptionField'] &&
                      <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />
                    }
                    <div className="setting">
                        <label>{I18n.t('field_file_formats', {scope: 'fe'})}</label>
                        <div className="checkbox-list">
                            <label>
                                <input type="hidden" name="acceptAnyFormat" defaultValue="false" />
                                <input type="checkbox" name="acceptAnyFormat" defaultChecked={acceptAnyFormat} defaultValue="true" onChange={this.updateSettings} />
                                {I18n.t('accept_any_format', {scope: 'fe'})}
                            </label>
                        </div>
                        {formatOptions}
                    </div>
                    <Setting.TextField name="maxFiles" label={I18n.t('max_files', {scope: 'fe'})} value={this.getMaxFiles()} onChange={this.updateSettings} description={I18n.t('field_max_files_description', {scope: 'fe'})} />
                    <Setting.TextField
                      name="reportKey"
                      label={I18n.t('field_report_key', {scope: 'fe'})}
                      value={this.props.content.reportKey}
                      description={I18n.t('field_report_key_description', {scope: 'fe'})}
                      {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                    />
                    <Setting.Checkbox
                      name="allowUploadLater"
                      label={I18n.t('field_allow_upload_later', {scope: 'fe'})}
                      description={I18n.t('field_allow_upload_later_description', {scope: 'fe'})}
                      value={this.props.content.allowUploadLater}
                      {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                    />
                    <Setting.Checkbox name="lockEdit" label={I18n.t('field_lock_edit', {scope: 'fe'})} description={I18n.t('field_lock_edit_description', {scope: 'fe'})} value={this.props.content.lockEdit} onChange={this.updateSettings} />
                    {(!this.props.settings['hideLockEditWhenPrefilled'] && AppConfig.features.form_prefill) &&
                      <Setting.Checkbox name="lockEditWhenPrefilled" label={I18n.t('field_lock_edit_when_prefilled', {scope: 'fe'})} description={I18n.t('field_lock_edit_when_prefilled_description', {scope: 'fe'})} value={this.props.content.lockEditWhenPrefilled} onChange={this.updateSettings} />
                    }
                    {!this.props.settings['hideContainsSensitiveInformation'] &&
                      <Setting.Checkbox
                        name="containsSensitiveInformation"
                        label={I18n.t('field_contains_sensitive_information', {scope: 'fe'})}
                        description={I18n.t('field_contains_sensitive_information_description', {scope: 'fe'})}
                        value={containsSensitiveInformation}
                        {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                      />
                    }
                    {this.renderPrettyViewSettings()}
                </div>
            );
        }

        async componentDidMount() {
            if (uploadManager.useUpdatedAttachmentComponent()) {
              await this.componentDidMountV2();
              return;
            }

            super.componentDidMount();
            if (this.props.mode == Mode.FILL && !this.isLocked) {
                window.fileUploader = {} // A global vairable to keep track of upload status. To disable FormCollection switchtab event when file is still uploading. 
                let uploadId = 'upload-' + Helpers.randomString();
                let zone = jQuery(this.dropzoneRef.current);
                let acceptedFiles = null;
                const maxFiles = this.getMaxFiles();

                if ((this.props.content.fileFormats || []).length > 0) {
                    acceptedFiles = [];
                    this.props.content.fileFormats.forEach((f) => {
                        (Attachment.FORMATS[f] || []).forEach(mime => acceptedFiles.push(mime));
                    });
                }

                // File drop/upload
                let uploadUrl = new URL(this.props.attachmentUploadUrl, window.location.origin)
                uploadUrl.searchParams.set('upload_id', uploadId);

                let dz = new Dropzone('#' + this.dropzoneRef.current.getAttribute('id'), {
                    url: uploadUrl.href,
                    headers: {"X-CSRF-Token": jQuery('meta[name="csrf-token"]').attr('content')},
                    maxFiles,
                    previewTemplate: zone.next('.preview-template').html(),
                    acceptedFiles: acceptedFiles ? acceptedFiles.join(',') : null,
                    dictInvalidFileType: acceptedFiles ? I18n.t('error_file_format', {
                        scope: 'fe',
                        formats: this.acceptedFormatList()
                    }) : undefined
                });

                dz.on('processing', function(file){
                    uploadUrl.searchParams.set('uuid', file?.upload?.uuid);
                    this.options.url = uploadUrl.href;
                    this.options.headers['X-CSRF-Token'] = jQuery('meta[name="csrf-token"]').attr('content')
                })

                dz.on('addedfile', (file) => this.addedFile(file, uploadId));
                
                dz.on('removedfile', (file) => {
                    if (file.size > 0) {
                        if (this.state.virusScanResult === 'OK') {
                            this.setState({ virusScanState: 'NOT_STARTED' });
                        }
                    }
                });

                dz.on('error', this.error);

                dz.on('success', (file, response) => {
                    if ($('.project-concept-note-form').hasClass('applicant-form')) {
                        Fathom.trackEvent('application attachment success');
                    }
                    
                    if (this.props.virusScannerEnabled) {
                        this.pollVirusScanResult(response.virusScanId);
                    }
                    this.updateFile(file.upload.uuid, { upload_status: 'complete' }, () => {});
                });

                dz.on('complete', this.complete);

                dz.on('uploadprogress', (f, p) => {
                    this.setState( prevState => ({
                        uploadProgress: { ...prevState.uploadProgress, [f.upload.uuid]: p}
                    }))
                })

                if (this.state.files.length >= maxFiles) {
                    dz.disable();
                }

                this._dz = dz;
            }
        }

        componentDidUpdate() {
            if (uploadManager.useUpdatedAttachmentComponent()) {
              this.componentDidUpdateV2();
              return;
            }

            super.componentDidUpdate();
            if (typeof this.props.setDisableSubmit == "function") {
                this.props.setDisableSubmit(this.fullFieldName(), this.state.files.some((f) => f.status == "added"));
            }

            if (!this.dropzoneRef.current?.dropzone)
                this.componentDidMount();
        }

        componentWillUnmount(){
            if (uploadManager.useUpdatedAttachmentComponent()) {
              this.componentWillUnmountV2();
              return;
            }

            if (this._dz) {
                this._dz.removeAllFiles(true);
                this._dz.destroy();
            }
        }

        beforeUpdateSettings(content) {
            content.required = castDataType(content.required, DataType.BOOLEAN);
            content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
            content.lockEditWhenPrefilled = castDataType(content.lockEditWhenPrefilled, DataType.BOOLEAN);
            content.allowUploadLater = castDataType(content.allowUploadLater, DataType.BOOLEAN);
            content.lockEdit = castDataType(content.lockEdit, DataType.BOOLEAN);
            content.acceptAnyFormat = castDataType(content.acceptAnyFormat, DataType.BOOLEAN);
            content.fileFormats = content.acceptAnyFormat ? [] : jQuery(this.refs.root).find('input[name="fileFormats"]:checked').map(((i, e) => e.value)).get();
            content.maxFiles = castDataType(content.maxFiles, DataType.INTEGER) || 1;
            this.beforeUpdateSettingsPrettyView(content);
        }

        addedFile = (file, uploadId) => {
            const { files } = this.state;
            const maxFiles =  this.getMaxFiles();

            if (file.size == 0) {
                alert(I18n.t('file_size_zero', {scope: 'fe'}));
                this._dz.removeFile(file);
            } else if (files.length >= maxFiles) {
                this._dz.removeFile(file);
            } else {
                window.fileUploader[file.upload.uuid] = 'in_progress'
                this.setState(
                    (prevState) => ({
                        files: prevState.files.concat([
                            { status: file.status, name: file.name, uploadId, uuid: file.upload.uuid, size: file.size, strId: Helpers.randomString(8) , upload_status: 'in_progress' },
                        ]),
                    }),
                    () => this.updateAnswer()
                );
            }
        }

        deleteFile = (e, idx) => {
            e.preventDefault();

            this.setState(
                (prevState) => {
                    return { files: update(prevState.files, { $splice: [[idx, 1]] }), deletedFiles: prevState.deletedFiles.concat([{ ...prevState.files[idx] }]) };
                },
                () => {
                    if (this.state.files.length < this.getMaxFiles()) {
                        this._dz.enable();
                        this.updateAnswer();
                    }
                }
            );
        }

        updateFile = (uuid, attrs, cb) => {
            this.setState((prevState) => {
                const idx = prevState.files.findIndex((f) => f.uuid == uuid);

                if (idx >= 0) {
                    const files = update(prevState.files, {
                        [idx]: {
                            $merge: attrs,
                        },
                    });
                    return { files };
                } else {
                    return {};
                }
            }, cb);
        }

        error = (file, response, xhr) => {
            if ($('.project-concept-note-form').hasClass('applicant-form')) {
                Fathom.trackEvent('application attachment error');
            }
            if (response.status == "login-required") {
                AutoSave.updateSaveStatus("session_error");
            }

            if (xhr && (xhr.status == 0 || xhr.status == 500)) {
                this.updateFile(file.upload.uuid, { error: xhr.status == 0 ? I18n.t("unexpected_network_error") : I18n.t("unexpected_error") });
            }

            if (!xhr) {
                this.updateFile(file.upload.uuid, { error: response, status: file.status });
            }
        }

        complete = (file) => {
            this._dz.removeFile(file);
            window.fileUploader[file.upload.uuid] = 'complete';
            this.updateFile(file.upload.uuid, { status: file.status }, () => {
                if (this.state.files.length >= this.getMaxFiles() && !this.state.files.some(f => f.status == "added")) {
                    this._dz.disable();
                }
            });
        }

        static markDeletedFile(fieldName, fieldScope, form) {
            form.append('<input type="hidden" value="' + fieldName + '" name="' + this.uploadInputNamePrefix(fieldScope) + '[deleted][]" class="form-component-attachments-deleted" />');
        }

        pollVirusScanResult(scanId) {
            this.setState({ virusScanState: 'PROCESSING' });
            jQuery('button[type="submit"]').attr('disabled','disabled')

            jQuery.ajax({
                type: 'GET',
                url: `/virus_scanner/get_scan_result/${scanId}`
            })
            .done(result => {
                if (result.result) {
                    this.setState({ virusScanState: 'FINISHED', virusScanResult: result.result === 'OK' ? 'OK' : 'INFECTED' })
                    jQuery('button[type="submit"]').removeAttr('disabled')
                } else {
                    setTimeout(this.pollVirusScanResult.bind(this, scanId), 1000);
                }
            })
            .fail(result => {
                this.setState({ virusScanState: 'FINISHED', virusScanResult: 'FAILED' });
            });
        }

        static uploadInputNamePrefix(fieldScope) {
            let name = 'attachments';

            if (fieldScope) {
                name = fieldScope + '[' + name + ']';
            }
            return name;
        }

        uploadInputNamePrefix() {
            return this.constructor.uploadInputNamePrefix(this.props.fieldScope);
        }

        fileFieldName(file) {
            // console.log(this.rowName, [this.getRawAnswer()].flat()[0] || "").split(":")[1]);
            this.rowName = this.props.repeatedFieldScope ? this.rowName || ([this.getRawAnswer()].flat()[0] || "").split(":")[1] || Helpers.randomString(8) : null;
            return [this.props.content.name, this.rowName, file.strId].filter(Boolean).join(":");
        }

        acceptedFormatList() {
            return this.props.content.fileFormats.map(f => {
                return I18n.t('file_format_' + f.toLowerCase(), { scope: 'fe' }) +
                    (Attachment.FORMAT_EXTENSIONS[f] ? ' (' + Attachment.FORMAT_EXTENSIONS[f] + ')' : '');
            }).join(', ');
        }

        getMaxFiles() {
            return this.props.content.maxFiles === void 0 ? 1 : this.props.content.maxFiles;
        }

        getFileSize(size) {
            let selectedSize = 0;
            let selectedUnit = "b";

            if (size > 0) {
                let units = ["tb", "gb", "mb", "kb", "b"];

                for (let i = 0; i < units.length; i++) {
                    let unit = units[i];
                    let cutoff = Math.pow(1000, 4 - i) / 10;

                    if (size >= cutoff) {
                        selectedSize = size / Math.pow(1000, 4 - i);
                        selectedUnit = unit;
                        break;
                    }
                }

                selectedSize = Math.round(10 * selectedSize) / 10; // Cutting of digits
            }

            return { size: selectedSize, unit: this.constructor.FILE_SIZE_UNITS[selectedUnit] };
        }

        getDownloadUrl(file) {
            const { attachmentDownloadUrl } = this.props;
            return attachmentDownloadUrl ? attachmentDownloadUrl.replace("__name__", this.fileFieldName(file)) + (this.props.content.isPrescoring ? '/prescoring' : '') : ""; 
        }

        getRawAnswer(context) {
            return [super.getRawAnswer(context)].flat().filter(Boolean);
        }

        getPrefilledAnswer(context) {
            return [super.getPrefilledAnswer(context)].flat().filter(Boolean);
        }

        mayUsePrefillAnswer() {
            // Checking if the files are the "same" from strId, as new file is copied saving the attachment_attr won't stay the same.
            // V2 answers are objects with attachment_id; V1 answers are "fieldname:strId" strings.
            const extractId = (a) => {
              if (typeof a !== 'string') return a?.attachment_id || a?.strId || null;
              return a.split(":")[this.props.repeatedFieldScope ? 2 : 1];
            };
            const prefillAnswer = this.getPrefilledAnswer().map(extractId);
            const answer = (uploadManager.useUpdatedAttachmentComponent() ? [...this.state.files, ...this.getRawAnswer()] : this.getRawAnswer()).map(extractId);
            if (Array.isArray(prefillAnswer) && Array.isArray(answer)) {
                return (
                  prefillAnswer.some((a) => !answer.includes(a)) ||
                  answer.some((a) => !prefillAnswer.includes(a))
                );
            }
            return prefillAnswer != answer;
        }

        getPrefillFiles() {
            const prefilledAnswer = this.getPrefilledAnswer();
            if (uploadManager.useUpdatedAttachmentComponent()) {
                return prefilledAnswer;
            }
            return prefilledAnswer
              .map((attr) => this.getPrefilledAttachments().find((f) => attr == f.attachment_attr))
              .filter(Boolean)
              .map((f) => ({
                strId: f.attachment_attr.split(":")[this.props.repeatedFieldScope ? 2 : 1] || "",
                ...f,
              }));
        }

        getFormattedPrefillAnswer() {
            if (uploadManager.useUpdatedAttachmentComponent()) {
                return this.getPrefillFiles().map(f => f.fileName).join('\n');
            }
            return this.getPrefillFiles().map((f) => f.name);
        }

        usePrefilledValue() {
            this.setState(
                (prevState) => {
                    const prefillFiles = this.getPrefillFiles();

                    return {
                        files: prefillFiles,
                        deletedFiles: [...prevState.deletedFiles, ...prevState.files].filter(
                            (f) => !prefillFiles.find((pf) => pf.strId == f.strId)
                        ),
                    };
                },
                () => {
                    if (uploadManager.useUpdatedAttachmentComponent()) {
                        this.updateAnswer(this.getPrefilledAnswer());
                    } else {
                        this.updateAnswer()
                    }
                }
            );
        }

  // ===============================================
  // Upload attachment using new UPLOAD MANAGER
  // ===============================================
  handleOpenModal = () => {
    this.setState({isModalOpen: true});
  };

  handleCloseModal = () => {
    this.setState({isModalOpen: false});
    closeModal(MODAL_ID);
  };

  renderViewV2() {
    const {uppy} = this.state;
    const {mode, content: {required, label_hidden, fileFormats}} = this.props;

    return (
      // Relative position for the counter
      <div style={{ position: "relative"}}>
        <label className={label_hidden ? 'hidden-label': ''}>
          <span className="field-label">{this.getText('label')}</span>
          {required ? <span className='required-field'> *</span> : null}
          {this.helpIcon()}
          {this.renderPrefillIcon()}
        </label>

        {this.renderFileCounter()}
        {
          [Mode.FILL, Mode.EDIT].includes(mode) && uppy && <div>
            <Dashboard
              id={this.uppyId}
              name={this.fullFieldName()}
              uppy={uppy}
              inline={true}
              hideUploadButton={true}
              doneButtonHandler={null}
              showProgressDetails={false}
              proudlyDisplayPoweredByUppy={false}
              showSelectedFiles={false}
              disabled={Mode.EDIT === mode}
              height={150}
              width='100%'
              data-upload-id={this.props.attachmentUploadId}
              data-upload-field={this.fullFieldName()}
            />
            {this.renderHiddenInputs()}
            {this.descriptionText()}
            {(fileFormats || []).length > 0 &&
              <p className="description">{I18n.t('accepted_file_formats', {scope: 'fe', formats: this.acceptedFormatList()})}</p>
            }
          </div>
        }
        {(this.props.missingAttachments && this.isUploadLaterEnabled()) ?
          <AddMissingAttachmentLink {...{
            isModalOpen: this.state.isModalOpen,
            handleOpenModal: this.handleOpenModal,
            handleCloseModal: this.handleCloseModal,
            label: this.getText('label'),
            description: this.descriptionText(),
            fullFieldName: this.fullFieldName(),
            fieldName: this.props.content.name,
            missingAttachmentsSaveUrl: this.props.missingAttachmentsSaveUrl,
            acceptedFormatList: this.acceptedFormatList(),
            renderHiddenInputs: this.renderHiddenInputs,
            renderAttachments: this.renderAttachments,
            uppyId: this.uppyId,
            uppy,
            fileFormats,
          }}/>
          :
          [Mode.FILL, Mode.EDIT, Mode.VIEW].includes(mode) && this.renderAttachments()
        }
      </div>
    );
  }

  // Key used to check if upload later is enabled
  allowUploadLaterFieldName() {
    return this.fullFieldName(false, '_upload_later');
  }

  // Check if upload later is enabled for the missing attachment
  isUploadLaterEnabled() {
    if (_.isEmpty(this.props.content)) return false;
    const fieldNamePath = Helpers.fieldNamePath(this.allowUploadLaterFieldName(), this.props.fieldScope);
    return castDataType(Helpers.getObjectValue(this.props.answers || {}, fieldNamePath), DataType.BOOLEAN);
  }

  renderHiddenInputs() {
    const answers = [...new Map([...this.state.files, ...this.getAnswer()].map(a => [a.id, a])).values()]
    if (answers.length === 0) {
      const {allowUploadLater} = this.props.content;
      const askUploadLater = allowUploadLater === true && !this.isUploadLaterEnabled();

      return (<>
        {askUploadLater && <input type='hidden' name={this.allowUploadLaterFieldName()} value={true}/>}
        <input type='hidden' name={this.fullFieldName(true)} value={null}/>
      </>);
    }

    return answers.map(file => {
      return Object.keys(file).map(prop => {
        if (typeof file[prop] != 'object') {
          return <input
            type='hidden'
            name={`${this.fullFieldName(true)}[${prop}]`}
            value={file[prop]}
            key={`[${prop}]-${file.id}`}
          />
        }
        return Object.keys(file[prop]||[]).map(nestedProp => {
          return <input
            type='hidden'
            name={`${this.fullFieldName(true)}[${prop}][${nestedProp}]`}
            value={file[prop][nestedProp]}
            key={`[${prop}][${nestedProp}]-${file.id}`}
          />
        })
      })
    })
  }

  renderAttachments() {
    const files = this.getUniqueFilesWithPendingUploads();
    if (this.props.mode === Mode.VIEW && files.length === 0) {
      return <div>{I18n.t('no_attachments_uploaded')}</div>
    }

    return files.map((f) => {
      const {size, unit} = this.getFileSize(f.metadata.size);

      if (Object.values(uploadManager.SCAN_STATUSES).includes(f.status)) {
        return (
          <div key={f.id} className='d-flex' style={{alignItems: 'baseline'}}>
            <div className={`uppy-preview uppy-file-preview`}>
              <div className="uppy-details">
                <span className="uppy-filename">
                    {f.metadata.filename}
                </span>
                <a href="#" className="btn btn-danger" data-dz-remove onClick={(e) => this.deleteFileV2(e, f.attachment_id)}>
                  <i className="icon-trash"/>
                </a>
              </div>
              <span>{this.renderScanStatus(f.status)}</span>
            </div>
          </div>
        )
      }

      const name = <a
        href={f.path}
        data-turbo="false"
        data-av="true"
        data-av-content-type={f.metadata.mime_type}
        data-av-show-download-link={f.path}
        data-av-title={f.metadata.filename}
      >
        {f.metadata.filename}
      </a>
      return (
        <div key={f.id} className='d-flex' style={{alignItems: 'baseline'}}>
          <div className={`uppy-preview uppy-file-preview`}>
            <div className="uppy-details">
              <span className="uppy-filename">
                {name}
                {f.metadata.size > 0 &&
                  <span className="uppy-size ml-1" data-uppy-size>
                   <strong>{size}</strong> {unit}
                  </span>
                }
              </span>
              {!this.isLocked && ([Mode.FILL, Mode.EDIT].includes(this.props.mode) || this.isUploadLaterEnabled()) && (
                <a href="#" className="btn btn-danger" data-dz-remove onClick={(e) => this.deleteFileV2(e, f.attachment_id)}>
                  <i className="icon-trash"/>
                </a>
              )}
            </div>
          </div>
        </div>
      );
    });
  }

  renderPrettyViewV2() {
    const files = JSON.parse(JSON.stringify(this.getAnswer()));
    const {hide = false, show_label = false} = this.props.content.prettyView || {};
    if (hide) return;

    return (<>
      {show_label &&
        <label>
          <span className="field-label">{this.getText('label')}</span>
        </label>
      }
      {files.map((f) => {
        const {size, unit} = this.getFileSize(f.metadata.size);
        const name = <a
          href={f.path}
          data-turbo="false"
          data-av="true"
          data-av-content-type={f.metadata.mime_type}
          data-av-show-download-link={f.path}
          data-av-title={f.metadata.filename}
        >
          {f.metadata.filename}
        </a>
        return (
          <div key={f.id} className='d-flex' style={{alignItems: 'baseline'}}>
            <div className={`uppy-preview uppy-file-preview`}>
              <div className="uppy-details">
                <span className="uppy-filename">
                  {name}
                  {f.metadata.size > 0 &&
                    <span className="uppy-size ml-1" data-uppy-size>
                      <strong>{size}</strong> {unit}
                    </span>
                  }
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </>)
  }

  renderFileCounterV2() {
    const files = this.getUniqueFilesWithPendingUploads();
    const maxFiles = this.getMaxFiles();
    return maxFiles && this.props.mode != Mode.VIEW && !this.isLocked ? (
      <span className="attachment-counter-count">
        <span className={`label label-${files.length < maxFiles ? "default" : "info"}`}>
          <span className="current">{files.length}</span> / <span className="limit">{maxFiles}</span> {I18n.t("files", {scope: "fe"}).toLowerCase()}
        </span>
      </span>
    ) : null;
  }

  async componentDidMountV2() {
    super.componentDidMount();

    const isNotPreviewModal = !$('.form-preview-modal').length;
    if (!this.isLocked && isNotPreviewModal && (this.props.mode === Mode.FILL || this.isUploadLaterEnabled())) {
      this._isMounted = true;

      await this.restorePendingUploads();
      if (!this._isMounted) return;

      this.uppyId = uploadManager.getUppyId(this.props.attachmentUploadId, this.fullFieldName());
      await this.initUppy();
    } else {
      const UppyModule = await import('@uppy/core');
      const uppy = new UppyModule.default({
        id: `uppy-view`,
        endpoint: '/files',     // path to tus server
        chunkSize: 2*1024*1024, // 2MB chunks
        removeFingerprintOnSuccess: true,
      })
      this.setState({uppy: uppy});
      this.uppyId = uploadManager.getUppyId(this.props.attachmentUploadId, this.props.content?.name);
    }
  }

  async componentDidUpdateV2(){
    super.componentDidUpdate();

    if (!this._isMounted) return;
    if (typeof this.props.setDisableSubmit == "function") {
      const isUploadInProgress = uploadManager.getActiveUploads(this.props.attachmentUploadId).length > 0;
      this.props.setDisableSubmit(this.fullFieldName(), isUploadInProgress);

      if (this.state.uppy) {
        const maxNumberOfFiles = this.getUniqueFilesWithPendingUploads().length;
        this.state.uppy.getPlugin(this.uppyId)?.setOptions({disabled: maxNumberOfFiles >= this.getMaxFiles()});
      }
    }
  }

  componentWillUnmountV2() {
    this._isMounted = false;
    if (this.uppyId) {
      uploadManager.uppyInstances.delete(this.uppyId);
    }

    // Cleanup all file subscriptions
    this.fileSubscriptions.forEach(unsubscribe => unsubscribe());
    this.fileSubscriptions.clear();

    // Cleanup listener to avoid duplicates after remount
    this.state.uppy?.off('done', this.handleDone);
  }

  deleteFileV2 = (e, attachment_id) => {
    e.preventDefault();
    let files = JSON.parse(JSON.stringify(this.getAnswer()));
    this.updateAnswer(files.filter(v => v.attachment_id !== attachment_id));
    this.removeFileFromState(attachment_id);
  }

  uppyError = (file, error, status) => {
    if (status === "login-required") {
      AutoSave.updateSaveStatus("session_error");
    }
    if (status && status !== 200) {
      window.alert(error)
    }
  }

  // The blank pointer submitted for an empty field is a placeholder, not a file, so
  // keep it out of everything that counts, renders or uploads attachments.
  getAnswer(context) {
    const answer = super.getAnswer(context);
    if (!uploadManager.useUpdatedAttachmentComponent() || !Array.isArray(answer)) {
      return answer;
    }

    return answer.filter(Boolean);
  }

  rawAnswerDefault() {
    if (uploadManager.useUpdatedAttachmentComponent()) {
      return []
    }
    super.rawAnswerDefault()
  }

  restorePendingUploads = async () => {
    const files = JSON.parse(JSON.stringify(this.getAnswer()));
    const pendingUploads = await uploadManager.getPendingUploads(this.props.attachmentUploadId, this.fullFieldName());
    const uploads = pendingUploads.filter(v => Object.values(uploadManager.SCAN_STATUSES).includes(v.status));
    this.setState({pendingUploads: uploads});

    const completedUploads = pendingUploads.filter(v => v.status === 'done');
    if (completedUploads.length > 0) {
      let answers = [...new Map([...files, ...completedUploads].filter(f => f?.id).map(f => [f.id, f])).values()];
      this.updateAnswer(answers);
    }
  }

  initUppy = async () => {
    if (!this._isMounted) return;

    const maxNumberOfFiles = this.getMaxFiles() - this.getAnswer().length;
    const allowedTypes = this.props.content.acceptAnyFormat ?
      Object.values(Attachment.FORMAT_EXTENSIONS).flatMap(v => v.split(',')) :
      (this.props.content.fileFormats || []).flatMap(f => Attachment.FORMAT_EXTENSIONS[f].split(',') )

    const uppy = await uploadManager.getUppyInstance(this.uppyId, {
      multiple: false,
      maxNumberOfFiles: maxNumberOfFiles,
      allowedTypes: allowedTypes.map(s => s.replace(/\s+/g, '')),
      onFileAdded: async (file) => {
        const {token} = await uploadManager.getOrCreateTokenForFile(file, this.props.attachmentUploadId, this.fullFieldName());
        file.meta = {...file.meta, token};

        // Subscribe to upload updates and store unsubscribe function for cleanup
        const unsubscribe = uploadManager.onUpdate(token, this.uppyId, ({status, attachment}) => {
          if (!this._isMounted) return;

          if (status === 'done') {
            this.updateAnswer([...this.getAnswer(), (({status, scan_id, ...rest}) => rest)(attachment)]);
          }
          this.setState(
            (prev) => ({
              pendingUploads: prev.pendingUploads.some(f => f.token === token)
                ? prev.pendingUploads.map(f =>
                  f.token === token
                    ? { ...f, ...attachment, token, status }
                    : f
                )
                : [...prev.pendingUploads, { ...attachment, token, status }]
            }),
            () => {
              this.updateUppyRestrictions();
            }
          );
        });

        // Store unsubscribe for cleanup
        this.fileSubscriptions.set(token, unsubscribe)
        return file;
      },
      onUploadSuccess: async (file, response) => {
        const permissions = this.props.imagePermissions || {};
        const result = await uploadManager.finalizeUpload(file, response, permissions);
        if (!result.ok) {
          this.uppyError(file, response.statusText, response.status)
        }
      }
    });

    // Attach done listener with proper context binding
    this.handleDone = this.handleDone.bind(this);
    uppy.on('done', this.handleDone);

    this.setState({uppy: uppy});
  }

  handleDone({attachment}) {
    this.updateAnswer([...this.getAnswer(), (({status, scan_id, ...rest}) => rest)(attachment)]);
  }

  removeFileFromState = (attachment_id) => {
    const file = [...this.state.pendingUploads, ...this.getAnswer()].find(f => f?.attachment_id === attachment_id);
    if (file?.token) {
      uploadManager.cancelUpload(file.token, this.state.uppy)
    }
    this.setState(prev => ({
      files: prev.files.filter(f => f.attachment_id !== attachment_id),
      pendingUploads: prev.pendingUploads.filter(f => f.attachment_id !== attachment_id)
    }));
    this.updateUppyRestrictions();
  }

  updateUppyRestrictions = () => {
    const remaining = this.getMaxFiles() - this.getUniqueFilesWithPendingUploads().length;
    const currentNumberOfFiles = this.state.uppy.getFiles().length;
    this.state.uppy.setOptions({
      restrictions: {
        maxNumberOfFiles: Math.max(1, remaining + currentNumberOfFiles),
      }
    });
  }

  getUniqueFilesWithPendingUploads = () => {
    const files = this.getAnswer() || [];
    const filteredPending = (this.state.pendingUploads || []).filter(v =>
      Object.values(uploadManager.SCAN_STATUSES).includes(v.status)
    );
    return [...this.state.files, ...files, ...filteredPending].filter(Boolean).filter((item, index, arr) => {
      if (!item?.attachment_id) return false;
      return arr.findIndex(f => f.attachment_id === item.attachment_id) === index;
    });
  }

  renderScanStatus(status) {
    switch (status) {
      case uploadManager.SCAN_STATUSES.SCANNING:
        return <span className="text-info"><i className="fa fa-gear fa-spin"/> Scanning for viruses...</span>;
      case uploadManager.SCAN_STATUSES.SCAN_FAILED:
        return <span className="text-warning"><i className="fa fa-times"/> Virus scan failed. Please reupload the file.</span>;
      case uploadManager.SCAN_STATUSES.INFECTED:
        return <span className="text-danger"><i className="fa fa-virus"/> Virus scan finished. Infected files were found and the upload was rejected.</span>
      default:
        return;
    }
  }
}

const AddMissingAttachmentLink = (
  {
    isModalOpen,
    handleOpenModal = () => {},
    handleCloseModal = () => {},
    label,
    description,
    fullFieldName,
    fieldName,
    missingAttachmentsSaveUrl,
    renderHiddenInputs = () => {},
    renderAttachments = () => {},
    uppyId,
    uppy,
    fileFormats,
    acceptedFormatList,
  }) => {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const formRef = useRef();

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);

    jQuery.post(`${missingAttachmentsSaveUrl}?field_name=${fieldName}`, jQuery(formRef.current).serialize())
      .done((_res) => {
        uploadManager.clearFieldUploads(fullFieldName).then(() => {
          setErrors([]);
          setSaving(false);
          handleCloseModal();
          Turbo.visit(window.location.href, {action: 'replace'});
        })
      })
      .fail((res) => {
        let responseJSON = res.responseJSON ? res.responseJSON.errors : {server: [{message: I18n.t('unexpected_error')}]};
        setErrors(responseJSON);
        setSaving(false);
      })
  };

  return (<>
    <div className='alert alert-warning mb-1'>
      <i className='fa fa-warning'/> {I18n.t('attachment_missing')}

      <a href='#' className='pull-right' onClick={handleOpenModal}>
        {I18n.t('add_attachment')}
      </a>
    </div>

    {isModalOpen &&
      <Portal
        id={MODAL_ID}
        title={I18n.t('add_attachment')}
        size='md'
        onSave={handleSave}
        saving={saving}
        onClose={handleCloseModal}
        cancelLabel={I18n.t('close')}
        component={ModalComponent}
      >
        <Errors errors={errors}/>
        <form ref={formRef} onSubmit={handleSave}>
          <div>
            <label>{label} *</label>
            <div>
              <Dashboard
                id={uppyId}
                name={fullFieldName}
                uppy={uppy}
                inline={true}
                hideUploadButton={true}
                doneButtonHandler={null}
                showProgressDetails={false}
                proudlyDisplayPoweredByUppy={false}
                showSelectedFiles={false}
                height={150}
                data-upload-field={fullFieldName}
              />
              {description}
              {(fileFormats||[]).length > 0 &&
                <p className='description'>{I18n.t('accepted_file_formats', {scope: 'fe', formats: acceptedFormatList})}</p>
              }
            </div>
            {renderAttachments()}
            {renderHiddenInputs()}
          </div>
        </form>
      </Portal>
    }
  </>)
};
